import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardNav from '../components/DashboardNav';
import MyStoryJourney from '../components/MyStoryJourney';
import StagePill from '../components/StagePill';
import NovaFeedSidebar from '../components/NovaFeedSidebar';
import { NovaUnifiedPageBar } from '../components/NovaUnified';
import YourCyclesCard from '../components/YourCyclesCard';
import { useApp } from '../context/AppContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useViewportConstrainedPanelHeight } from '../hooks/useViewportConstrainedPanelHeight';
import { MoodTracker, CycleCalendar } from '../components/logging';
import { IVF_STEPS, IVF_STEP_PREP, CHAPTERS, getMedicationVideo } from '../data';
import { MobileDashboardHeader, MobileNavDrawer } from '../components/MobileDashboardChrome';
import {
  btnPrimary,
  fertilityDemoEditButton,
  introCardKeyline,
  introHeadlineSerifDeep,
  pageTitleH1,
  demoFlowPageDescriptor,
  DASHBOARD_NAV_WIDTH_PX,
  layoutMobileMediaQuery,
  demoShellMobileMediaQuery,
  chapterDemoMainPadding,
  DEMO_SECTION_STACK_GAP_PX,
  DEMO_PRODUCT_MAIN_PADDING_PX,
  demoSectionCardStackStyle,
  DEMO_PRODUCT_INPUT_SCOPE_CLASS,
  FERTILITY_DEMO_SECTION_BORDER,
} from '../designSystem';
import PageHeader from '../components/PageHeader';
import SectionHeader from '../components/SectionHeader';
import ChapterShellPageHeader from '../components/ChapterShellPageHeader';
import NovaSidebarWithCollapse from '../components/NovaSidebarWithCollapse';
import { getPageHeaderProps } from '../data/pageHeaderConfig';
import { formatIvfTimelineCallout, formatStimulationRangeHeadline, ivfTimelineFromCycle } from '../utils/ivfCycleTimeline';
import { mergeDemoFertilityMyStoryTimeline } from '../data/demoFertilityMyStoryTimeline';

const SIDEBAR_BREAKPOINT = 1100; // below this, sidebar becomes overlay

const CHAPTER_PALETTE = {
  ivf: { accent: '#D4537E', bg: '#FBEAF0' },
  fertility: { accent: '#D4537E', bg: '#FBEAF0' },
  ttc: { accent: '#7F77DD', bg: '#EEEDFE' },
  pregnancy: { accent: '#14B8A6', bg: '#F0FDFA' },
  postpartum: { accent: '#9B2942', bg: '#F5EAED' },
  baby: { accent: '#9B2942', bg: '#F5EAED' },
  surrogacy: { accent: '#059669', bg: '#EAF3DE' },
  loss: { accent: '#185FA5', bg: '#E6F1FB' },
};

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 21) return 'evening';
  return 'night';
};

const NOVA_CHECK_IN_IVF = {
  morning: { contextPill: 'Morning briefing', text: 'How are you feeling today? Your injections are at 9pm tonight — set a reminder if you haven\'t.' },
  afternoon: { contextPill: 'Daily check-in', text: 'How are you feeling after this morning? Drink plenty of water today. Your injections are at 9pm tonight.' },
  evening: { contextPill: 'Daily check-in', text: 'Injection time is coming up. Need to talk through anything before your 9pm dose?' },
  night: { contextPill: 'Daily check-in', text: 'Hope today went well. Rest up — Nova is here if you need anything.' },
};

const NOVA_CHECK_IN_GENERIC = {
  morning: { contextPill: 'Morning briefing', text: 'Good morning. How are you feeling today? Nova is here whenever you need support.' },
  afternoon: { contextPill: 'Daily check-in', text: 'How are you doing? Take care of yourself today — Nova is here if you want to talk.' },
  evening: { contextPill: 'Daily check-in', text: 'Winding down? Nova is here if you need anything before bed.' },
  night: { contextPill: 'Daily check-in', text: 'Hope today went well. Rest up — Nova is here if you need anything.' },
};

const STEP_SUBTITLES = {
  retrieval: "The biggest day of your cycle — you've got this.",
  stimulation: "Your body is building follicles — take it one night at a time.",
  baseline: "You're officially in the cycle — today sets the plan.",
  transfer: "Embryo transfer day — breathe and trust the process.",
  beta: "Results day — whatever the news, Nova is here for you.",
  fertilization: "The lab is working — you get to rest today.",
  consult: "Your first step — we've got you prepared.",
};

/** Display YYYY-MM-DD from clinic schedule inputs */
function formatClinicStepDate(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STEP_WHAT_TO_EXPECT = {
  retrieval: [
    { emoji: '🏥', title: 'Arrive 30 min early', sub: "Bring ID, insurance card, comfy clothes" },
    { emoji: '💉', title: 'Sedation or light anaesthesia', sub: "Procedure is 20–30 min, you'll sleep through it" },
    { emoji: '🛌', title: 'Recovery 1–2 hours after', sub: "You'll need a driver home — no driving yourself" },
  ],
  stimulation: [
    { emoji: '💉', title: 'Nightly injections', sub: 'Same time each night — set an alarm' },
    { emoji: '📅', title: 'Monitoring appointments', sub: 'Bloodwork + ultrasound every 2–3 days' },
    { emoji: '😴', title: 'Rest and hydrate', sub: "Your body is working hard — be gentle with yourself" },
  ],
  baseline: [
    { emoji: '🩸', title: 'Bloodwork + ultrasound', sub: 'Checking hormone levels and antral follicle count' },
    { emoji: '⏰', title: 'Fasting may be required', sub: 'Check with your clinic the day before' },
    { emoji: '📋', title: 'Protocol review', sub: 'Your doctor confirms your stim plan today' },
  ],
  transfer: [
    { emoji: '🛏', title: 'Rest the night before', sub: 'Light activity only — no strenuous exercise' },
    { emoji: '💊', title: 'Take your progesterone', sub: 'Exactly as prescribed — timing matters' },
    { emoji: '🚗', title: 'Bring a driver', sub: "You'll want someone with you for support" },
  ],
  beta: [
    { emoji: '🩸', title: 'Blood draw only', sub: 'No urine tests — blood beta is more accurate' },
    { emoji: '⏳', title: 'Results in a few hours', sub: 'Clinic usually calls by early afternoon' },
    { emoji: '💙', title: 'Either result is valid', sub: "Nova is here for you whatever the news is" },
  ],
  fertilization: [
    { emoji: '🧫', title: 'No action needed today', sub: "Embryologists are watching your eggs" },
    { emoji: '📞', title: 'Clinic calls with update', sub: 'How many fertilized — usually by morning' },
    { emoji: '📊', title: 'Day 3 or Day 5 check next', sub: 'Embryos graded on development progress' },
  ],
  consult: [
    { emoji: '📋', title: 'Bring your full history', sub: 'Previous cycle records, bloodwork, AMH' },
    { emoji: '❓', title: 'Prepare your questions', sub: 'Ask Nova to help you build your question list' },
    { emoji: '👩‍⚕️', title: 'Meet your care team', sub: 'RE, nurses, and financial coordinator' },
  ],
};

const STEP_WATCH_FOR = {
  retrieval: [
    { emoji: '⚠️', title: 'Signs of OHSS', sub: 'Severe bloating, pain, shortness of breath → call clinic immediately' },
    { emoji: '🧫', title: 'Fertilization report tomorrow', sub: 'Clinic calls with how many eggs fertilized' },
    { emoji: '📅', title: 'Next: Day 3 or Day 5 check', sub: 'Embryo development update coming soon' },
  ],
  stimulation: [
    { emoji: '📈', title: 'Follicles growing', sub: 'Target 16–20mm before trigger shot' },
    { emoji: '🌡', title: 'Watch for OHSS signs', sub: 'Bloating or pain — call your clinic' },
    { emoji: '💉', title: 'Trigger shot timing', sub: 'Nova will remind you — timing is critical' },
  ],
  baseline: [
    { emoji: '💊', title: 'Start medications today', sub: 'First injections usually begin tonight' },
    { emoji: '📞', title: 'Clinic calls with protocol', sub: 'Confirm doses and injection timing' },
    { emoji: '🔔', title: 'Set your injection alarm', sub: 'Same time every night from here' },
  ],
  transfer: [
    { emoji: '🤞', title: 'Embryo thaw confirmation', sub: "Clinic confirms embryo survived thaw morning-of" },
    { emoji: '🛋', title: 'Rest after transfer', sub: 'Light activity for 24 hours — no heavy lifting' },
    { emoji: '⏳', title: 'Beta HCG in 10–14 days', sub: "The wait begins — Nova will check in on you" },
  ],
  beta: [
    { emoji: '💛', title: 'If positive', sub: 'Repeat beta in 48 hrs to confirm doubling' },
    { emoji: '💙', title: 'If negative', sub: "Nova is here — you don't have to process this alone" },
    { emoji: '📅', title: 'Next cycle conversation', sub: 'Ask your RE about what changes for next time' },
  ],
  fertilization: [
    { emoji: '📊', title: 'Normal fertilization rate', sub: '70–80% of mature eggs typically fertilize' },
    { emoji: '🔬', title: 'Embryo grading coming', sub: 'Day 3 or Day 5 — ask Nova what grades mean' },
    { emoji: '❄️', title: 'Freeze decision ahead', sub: 'Your RE will discuss fresh vs frozen transfer' },
  ],
  consult: [
    { emoji: '📝', title: 'Treatment plan follows', sub: 'RE outlines your protocol within a few days' },
    { emoji: '💰', title: 'Financial review', sub: 'Insurance, grants, and financing options' },
    { emoji: '🔬', title: 'Additional testing?', sub: 'AMH, ERA, or genetic testing may be recommended' },
  ],
};

function mapJourneyTypeToBucketId(journeyType) {
  const j = String(journeyType || '').toLowerCase();
  if (j === 'menopause') return 'postpartum';
  if (j === 'cycle' || j === 'first-chapter') return 'ttc';
  const m = {
    ivf: 'ivf', ttc: 'ttc', surrogacy: 'surrogacy', pregnancy: 'pregnancy',
    postpartum: 'postpartum', baby: 'baby', loss: 'loss',
  };
  if (m[j]) return m[j];
  if (j === 'fertility') return 'ivf';
  return 'ivf';
}

function nextReminderDate(timeHHmm) {
  const [hh, mm] = String(timeHHmm || '09:00').split(':');
  const now = new Date();
  const next = new Date(now);
  next.setHours(Number(hh) || 9, Number(mm) || 0, 0, 0);
  if (next.getTime() <= now.getTime()) next.setDate(next.getDate() + 1);
  return next;
}

function formatTime24to12(timeStr) {
  const [h, mm] = (timeStr || '09:00').split(':');
  const hh = parseInt(h, 10) || 9;
  const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
  return `${h12}:${(mm || '00').padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`;
}

const SHELL_FONT = '"Plus Jakarta Sans", -apple-system, sans-serif';

/**
 * Single merged gradient header for /demo/fertility/myjourney — matches ChapterShellPageHeader on other demo pages.
 */
function DemoFertilityMyJourneyMergedHeader({ navigate, journey, activeCycle, cyclesCardRef, palette, novaCheckIn }) {
  const headerProps = getPageHeaderProps('fertility', 'myjourney', navigate);
  if (!headerProps) return null;

  const P = palette.accent;
  const joinBtn = (
    <button
      type="button"
      onClick={() => headerProps.onSignUp?.('/signup?source=journey')}
      style={{
        ...btnPrimary,
        fontFamily: SHELL_FONT,
        background: P,
      }}
    >
      Join the waitlist
    </button>
  );

  const cycles = journey?.cycles || [];
  const currentCycle = activeCycle;
  const stimDay = currentCycle?.stimDay ?? (currentCycle?.startedAt ? Math.max(0, Math.floor((new Date() - new Date(currentCycle.startedAt + 'T00:00:00')) / 86400000)) : null);
  const stimDayStart = currentCycle?.stimDayStart != null && currentCycle.stimDayStart > 0 ? currentCycle.stimDayStart : 1;
  const hasStimDay = stimDay != null && stimDay > 0;
  const formattedStartDate = currentCycle?.startedAt
    ? (() => {
        const d = new Date(currentCycle.startedAt + 'T00:00:00');
        return Number.isNaN(d.getTime()) ? currentCycle.startedAt : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      })()
    : null;
  const protocol = currentCycle?.protocol || null;
  const clinic = currentCycle?.clinic || null;
  const estimatedRetrieval = currentCycle?.estimatedRetrieval || currentCycle?.retrievalDate || null;
  const timeline = currentCycle && cycles.length > 0 ? ivfTimelineFromCycle(currentCycle, cycles) : null;
  const follicleCount = currentCycle?.lastScan?.follicleCount ?? currentCycle?.follicleCount ?? null;
  const currentMedication = (currentCycle?.medications || [])[0];
  const medicationName = currentMedication ? [currentMedication.name, currentMedication.dose, currentMedication.units].filter(Boolean).join(' ') : null;
  const deriveStage = () => {
    if (currentCycle?.stage && !['ivf', 'fertility'].includes(String(currentCycle.stage))) return currentCycle.stage;
    const today = new Date();
    const retrievalDate = estimatedRetrieval ? new Date(estimatedRetrieval + 'T00:00:00') : null;
    const transferDate = currentCycle?.transferDate ? new Date(currentCycle.transferDate + 'T00:00:00') : null;
    if (transferDate && today >= transferDate) return 'Two-week wait';
    if (retrievalDate && today >= retrievalDate) return 'Post-retrieval';
    if (hasStimDay && stimDay <= 12) return 'Stimming';
    if (hasStimDay) return 'Stimming';
    return 'Cycle in progress';
  };
  const currentStage = currentCycle?.stage === 'ivf' || currentCycle?.stage === 'fertility' ? deriveStage() : currentCycle?.stage || 'Cycle in progress';
  const subtextParts = [formattedStartDate, protocol, clinic].filter(Boolean);
  const subtext = subtextParts.join(' · ') || null;

  const openLogCycle = () => cyclesCardRef.current?.openNewCycle?.();
  const openEditCycle = () => cyclesCardRef.current?.openEditCycle?.(currentCycle);

  const ghostChipBtn = {
    background: 'rgba(255,255,255,0.6)',
    border: '0.5px solid rgba(212,83,126,0.15)',
    borderRadius: 999,
    padding: '4px 12px',
    fontSize: 11,
    color: 'rgba(60,30,45,0.4)',
    cursor: 'pointer',
    fontFamily: SHELL_FONT,
  };

  let pageTitle = headerProps.pageTitle;
  let subtitle = headerProps.subtitle;
  let pills = headerProps.statusCard?.pills ? [...headerProps.statusCard.pills] : [];
  let stat = headerProps.statusCard?.stat;
  let statCallout;

  let trailing = (
    <>
      <StagePill chapter="fertility" accent={palette.accent} background={palette.bg} />
      {joinBtn}
    </>
  );

  if (!currentCycle || cycles.length === 0) {
    pageTitle = 'Your IVF journey starts here';
    subtitle = 'Log your first cycle to track stims, results, and outcomes';
    pills = [
      { label: 'Cycles' },
      { label: 'Medications' },
      { label: 'Results' },
      { label: 'Milestones' },
    ];
    stat = undefined;
    statCallout = undefined;
    trailing = (
      <>
        <StagePill chapter="fertility" accent={palette.accent} background={palette.bg} />
        <button type="button" onClick={openLogCycle} style={{ ...ghostChipBtn, fontWeight: 500, color: '#D4537E', border: '0.5px solid rgba(212,83,126,0.25)', whiteSpace: 'nowrap' }}>
          Start logging →
        </button>
        {joinBtn}
      </>
    );
  } else if (!hasStimDay) {
    pageTitle = 'Cycle started';
    subtitle = subtext || headerProps.subtitle;
    pills = [{ label: 'Cycle in progress', dot: true, dotColor: 'bg-rose-400' }];
    stat =
      timeline?.daysUntil != null
        ? {
            value: `~${timeline.daysUntil}`,
            label: timeline.countdownTarget === 'transfer' ? 'days to transfer' : 'days to retrieval',
          }
        : { value: '—', label: 'days to retrieval' };
    statCallout = timeline ? formatIvfTimelineCallout(timeline) : undefined;
    trailing = (
      <>
        <StagePill chapter="fertility" accent={palette.accent} background={palette.bg} />
        <button type="button" onClick={openEditCycle} style={ghostChipBtn}>
          + Add stim day
        </button>
        <button type="button" onClick={openEditCycle} style={ghostChipBtn}>
          + Add follicle count
        </button>
        {joinBtn}
      </>
    );
  } else {
    pageTitle = formatStimulationRangeHeadline(stimDay, stimDayStart);
    subtitle = subtext || headerProps.subtitle;
    pills = [
      { label: currentStage, dot: true, dotColor: 'bg-rose-400' },
      ...(follicleCount > 0 ? [{ label: `${follicleCount} follicles last scan` }] : []),
      ...(medicationName ? [{ label: medicationName, emoji: '💊' }] : []),
    ];
    stat =
      timeline?.daysUntil != null
        ? {
            value: `~${timeline.daysUntil}`,
            label: timeline.countdownTarget === 'transfer' ? 'days to transfer' : 'days to retrieval',
          }
        : headerProps.statusCard?.stat;
    statCallout = timeline ? formatIvfTimelineCallout(timeline) : undefined;
  }

  const novaMessage = {
    context: novaCheckIn.contextPill,
    message: novaCheckIn.text,
  };

  return (
    <ChapterShellPageHeader
      {...headerProps}
      pageTitle={pageTitle}
      subtitle={subtitle}
      statusCard={{
        ...headerProps.statusCard,
        title: '',
        subtitle: '',
        pills,
        stat,
        ...(statCallout ? { statCallout } : {}),
      }}
      trailing={trailing}
      novaMessage={novaMessage}
    />
  );
}

export default function Journey() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo/');
  const mNav = useMediaQuery(isDemo ? demoShellMobileMediaQuery : layoutMobileMediaQuery);
  const {
    user,
    journey,
    activeCycle,
    ensureActiveCycle,
    setActiveCycleId,
    addCycle,
    addMedication,
    removeMedication,
    addReminder,
    removeReminder,
    setNovaDraft,
    updateCycle,
    addLog,
    getJourneyTimeline,
    updateJourneyTimeline,
    journeyTimelines,
    novaPresenceBar,
  } = useApp();

  useEffect(() => {
    ensureActiveCycle();
  }, [ensureActiveCycle]);

  const stage = user?.journeyType || 'ivf';
  const isIVF = stage === 'ivf';
  const isTTC = stage === 'ttc';

  const [selectedChapterId, setSelectedChapterId] = useState(() => mapJourneyTypeToBucketId(stage));
  useEffect(() => {
    setSelectedChapterId(mapJourneyTypeToBucketId(stage));
  }, [stage]);

  const chapter = useMemo(() => {
    return CHAPTERS.find(c => c.id === selectedChapterId) || CHAPTERS.find(c => c.id === 'ivf') || CHAPTERS[0];
  }, [selectedChapterId]);

  const [currentStep, setCurrentStep] = useState(3); // default: retrieval
  const [novaPanelOpen, setNovaPanelOpen] = useState(true);
  const [sidebarOverlayOpen, setSidebarOverlayOpen] = useState(false);
  const wideEnough = useMediaQuery(`(min-width: ${SIDEBAR_BREAKPOINT}px)`);
  const SIDEBAR_WIDTH = 320;
  const presenceKey = novaPresenceBar ? 1 : 0;
  const { ref: rightRailRef, height: rightRailHeight } = useViewportConstrainedPanelHeight(presenceKey);
  const [newMed, setNewMed] = useState({ name: '', dose: '', units: '', schedule: '', startDay: '', endDay: '', reminderTime: '' });
  const [newReminder, setNewReminder] = useState({ title: '', time: '09:00', linkedMedicationId: '' });
  const cyclesCardRef = useRef(null);
  const cycleNotesRef = useRef(null);
  const [notifPermission, setNotifPermission] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission || 'default';
  });
  const reminderTimeoutsRef = useRef([]);

  const cycleLabel = activeCycle?.name || 'Cycle 1';
  const startedAt = activeCycle?.startedAt || '—';

  const fertileWindow = useMemo(() => {
    if (!isTTC || !activeCycle?.startedAt) return null;
    const start = new Date(activeCycle.startedAt + 'T00:00:00');
    if (Number.isNaN(start.getTime())) return null;
    const cycleLength = Number(activeCycle.cycleLengthDays || 28);
    const ovulationDay = Math.max(10, Math.min(cycleLength - 10, cycleLength - 14));
    const fwStart = new Date(start);
    fwStart.setDate(fwStart.getDate() + (ovulationDay - 5));
    const fwEnd = new Date(start);
    fwEnd.setDate(fwEnd.getDate() + (ovulationDay + 1));
    return { cycleLength, ovulationDay, fwStart, fwEnd };
  }, [activeCycle?.cycleLengthDays, activeCycle?.startedAt, isTTC]);

  const showIVF = selectedChapterId === 'ivf';
  const showTTC = selectedChapterId === 'ttc';
  const showPregnancy = selectedChapterId === 'pregnancy';
  const showPostpartum = selectedChapterId === 'postpartum';
  const showBaby = selectedChapterId === 'baby';
  const showSurrogacy = selectedChapterId === 'surrogacy';
  const showLoss = selectedChapterId === 'loss';

  const demoProductInputScope = /^\/demo\/(fertility|ttc|trying-to-conceive)(\/|$)/.test(location.pathname);

  useEffect(() => {
    reminderTimeoutsRef.current.forEach(t => clearTimeout(t));
    reminderTimeoutsRef.current = [];
    if (!(showIVF || showTTC)) return;
    if (!activeCycle?.reminders?.length) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;

    // Best-effort: schedule while the tab is open.
    (activeCycle.reminders || []).forEach(r => {
      const at = nextReminderDate(r.time);
      const delay = at.getTime() - Date.now();
      if (delay <= 0 || delay > 1000 * 60 * 60 * 36) return;
      const t = setTimeout(() => {
        try {
          new Notification(r.title || 'Reminder', {
            body: r.linkedMedicationId
              ? `Time: ${r.time} · ${(activeCycle.medications || []).find(m => m.id === r.linkedMedicationId)?.name || 'Medication'}`
              : `Time: ${r.time}`,
          });
        } catch {
          // ignore
        }
      }, delay);
      reminderTimeoutsRef.current.push(t);
    });

    return () => {
      reminderTimeoutsRef.current.forEach(t => clearTimeout(t));
      reminderTimeoutsRef.current = [];
    };
  }, [activeCycle?.medications, activeCycle?.reminders, showIVF, showTTC]);

  const rightSidebar = !isDemo && wideEnough ? (
    <aside
      ref={rightRailRef}
      className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
      style={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        alignSelf: 'start',
        minHeight: 0,
        maxHeight: rightRailHeight,
        background: 'var(--white)',
        borderLeft: '0.5px solid rgba(212, 83, 126, 0.12)',
        overflow: 'hidden',
        position: 'sticky',
        top: 0,
        height: rightRailHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flex: '1 1 0%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <NovaFeedSidebar
          navigate={navigate}
          setNovaDraft={setNovaDraft}
          chapter={showTTC ? 'ttc' : 'fertility'}
          novaOpen={novaPanelOpen}
          onNovaOpenChange={setNovaPanelOpen}
        />
      </div>
    </aside>
  ) : !isDemo ? (
    <>
      {/* Narrow viewport: floating button to open sidebar overlay */}
      <button
        type="button"
        onClick={() => setSidebarOverlayOpen(true)}
        title="Open sidebar"
        style={{
          position: 'fixed',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 40,
          height: 40,
          borderRadius: 12,
          border: '1px solid var(--border)',
          background: 'var(--white)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          color: 'var(--pink)',
          zIndex: 100,
        }}
      >
        ‹
      </button>
      {/* Sidebar overlay */}
      {sidebarOverlayOpen && (
        <>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSidebarOverlayOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setSidebarOverlayOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.35)',
              zIndex: 200,
            }}
            aria-label="Close sidebar"
          />
          <aside
            className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: Math.min(SIDEBAR_WIDTH, 320),
              maxWidth: '90vw',
              height: '100vh',
              background: 'var(--white)',
              borderLeft: '0.5px solid rgba(212, 83, 126, 0.12)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 201,
              boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
            }}
          >
            <button
              type="button"
              onClick={() => setSidebarOverlayOpen(false)}
              title="Close sidebar"
              style={{
                position: 'absolute',
                left: 12,
                top: 16,
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '1px solid var(--border)',
                background: 'var(--white)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                color: 'var(--muted)',
                zIndex: 10,
              }}
            >
              ×
            </button>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                paddingTop: 8,
              }}
            >
              <NovaFeedSidebar
                navigate={navigate}
                setNovaDraft={setNovaDraft}
                chapter={showTTC ? 'ttc' : 'fertility'}
                novaOpen={novaPanelOpen}
                onNovaOpenChange={setNovaPanelOpen}
              />
            </div>
          </aside>
        </>
      )}
    </>
  ) : null;

  const gridCols = isDemo
    ? `${DASHBOARD_NAV_WIDTH_PX}px minmax(0, 1fr)`
    : wideEnough
      ? `${DASHBOARD_NAV_WIDTH_PX}px 1fr ${SIDEBAR_WIDTH}px`
      : `${DASHBOARD_NAV_WIDTH_PX}px 1fr`;

  const palette = CHAPTER_PALETTE[stage] || CHAPTER_PALETTE.ivf;

  const myStoryTimeline = useMemo(() => {
    const base = getJourneyTimeline(user);
    if (isDemo && showIVF) return mergeDemoFertilityMyStoryTimeline(base);
    return base;
  }, [isDemo, showIVF, user?.email, journeyTimelines, getJourneyTimeline, user]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const timeOfDay = getTimeOfDay();
  const novaCheckInIVF = NOVA_CHECK_IN_IVF[timeOfDay] || NOVA_CHECK_IN_IVF.afternoon;
  const novaCheckInGeneric = NOVA_CHECK_IN_GENERIC[timeOfDay] || NOVA_CHECK_IN_GENERIC.afternoon;

  return (
    <>
    <div
      className="demo-layout-grid"
      style={{
      display: 'grid',
      minHeight: '100vh',
      background: 'var(--sand)',
      fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
      ...(mNav
        ? { gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateRows: 'auto minmax(0, 1fr)' }
        : { gridTemplateColumns: gridCols, transition: 'grid-template-columns 0.2s ease' }),
    }}
    >
      {mNav ? <MobileDashboardHeader onMenu={() => setMobileMenuOpen(true)} /> : <DashboardNav />}
      <main
        className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
        style={{
          overflowY: 'auto',
          padding: isDemo ? chapterDemoMainPadding(mNav) : DEMO_PRODUCT_MAIN_PADDING_PX,
          minWidth: 0,
          minHeight: 0,
          boxSizing: 'border-box',
        }}
      >
      <div
        className={isDemo ? 'demo-shell-main-inner' : undefined}
        style={{ display: 'flex', flexDirection: 'column', gap: DEMO_SECTION_STACK_GAP_PX, width: '100%', boxSizing: 'border-box' }}
      >

        {/* Header — demo fertility IVF: one merged gradient shell (matches other /demo/fertility/* pages) */}
        {isDemo && showIVF ? (
          <DemoFertilityMyJourneyMergedHeader
            navigate={navigate}
            journey={journey}
            activeCycle={activeCycle}
            cyclesCardRef={cyclesCardRef}
            palette={palette}
            novaCheckIn={novaCheckInIVF}
          />
        ) : (
          <div>
            <div>
              {isDemo ? (
                <PageHeader
                  className="page-header-block--section-stack"
                  chapter="fertility"
                  eyebrow="Fertility · My Journey"
                  title="Your IVF story"
                  descriptor="Cycles, stims, and milestones — Nova uses this to personalize your support."
                  trailing={
                    <>
                      <StagePill chapter="fertility" accent={palette.accent} background={palette.bg} />
                      <button type="button" onClick={() => navigate('/signup?source=journey')} style={{ ...btnPrimary, background: palette.accent }}>
                        Join the waitlist
                      </button>
                    </>
                  }
                />
              ) : (
                <>
                  <h1 style={{ ...pageTitleH1, marginBottom: 6 }}>
                    My Journey
                  </h1>
                  <p style={demoFlowPageDescriptor}>Your full journey — story, cycles, medications, and milestones</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Chapter badge / IVF smart status header (separate gradient — not demo fertility, which uses merged shell above) */}
        {showIVF ? !(isDemo && showIVF) ? (() => {
          const cycles = journey?.cycles || [];
          const currentCycle = activeCycle;
          const cycleNumber = cycles.length;
          const stimDay = currentCycle?.stimDay ?? (currentCycle?.startedAt ? Math.max(0, Math.floor((new Date() - new Date(currentCycle.startedAt + 'T00:00:00')) / 86400000)) : null);
          const stimDayStart = currentCycle?.stimDayStart != null && currentCycle.stimDayStart > 0 ? currentCycle.stimDayStart : 1;
          const hasStimDay = stimDay != null && stimDay > 0;
          const formattedStartDate = currentCycle?.startedAt ? (() => { const d = new Date(currentCycle.startedAt + 'T00:00:00'); return Number.isNaN(d.getTime()) ? currentCycle.startedAt : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); })() : null;
          const protocol = currentCycle?.protocol || null;
          const clinic = currentCycle?.clinic || null;
          const estimatedRetrieval = currentCycle?.estimatedRetrieval || currentCycle?.retrievalDate || null;
          const daysToRetrieval = estimatedRetrieval ? Math.ceil((new Date(estimatedRetrieval + 'T23:59:59') - new Date()) / 86400000) : null;
          const follicleCount = currentCycle?.lastScan?.follicleCount ?? currentCycle?.follicleCount ?? null;
          const currentMedication = (currentCycle?.medications || [])[0];
          const medicationName = currentMedication ? [currentMedication.name, currentMedication.dose, currentMedication.units].filter(Boolean).join(' ') : null;
          const deriveStage = () => {
            if (currentCycle?.stage && !['ivf', 'fertility'].includes(String(currentCycle.stage))) return currentCycle.stage;
            const today = new Date();
            const retrievalDate = estimatedRetrieval ? new Date(estimatedRetrieval + 'T00:00:00') : null;
            const transferDate = currentCycle?.transferDate ? new Date(currentCycle.transferDate + 'T00:00:00') : null;
            if (transferDate && today >= transferDate) return 'Two-week wait';
            if (retrievalDate && today >= retrievalDate) return 'Post-retrieval';
            if (hasStimDay && stimDay <= 12) return 'Stimming';
            if (hasStimDay) return 'Stimming';
            return 'Cycle in progress';
          };
          const currentStage = currentCycle?.stage === 'ivf' || currentCycle?.stage === 'fertility' ? deriveStage() : (currentCycle?.stage || 'Cycle in progress');
          const subtextParts = [formattedStartDate, protocol, clinic].filter(Boolean);
          const subtext = subtextParts.join(' · ') || null;
          const openLogCycle = () => cyclesCardRef.current?.openNewCycle?.();
          const openEditCycle = () => cyclesCardRef.current?.openEditCycle?.(currentCycle);

          const cardStyle = { background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', ...introCardKeyline, padding: '22px 26px' };
          const labelStyle = { fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#993556', marginBottom: 4 };
          const headlineStyle = { ...introHeadlineSerifDeep, lineHeight: 1.2 };
          const subtextStyle = { fontSize: 12, color: 'rgba(60,30,45,0.5)', marginTop: 3 };

          if (!currentCycle || cycles.length === 0) {
            return (
              <div style={cardStyle}>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={labelStyle}>IVF · Fertility chapter</div>
                    <div style={headlineStyle}>Your IVF journey starts here</div>
                    <div style={subtextStyle}>Log your first cycle to track stims, results, and outcomes</div>
                  </div>
                  <button type="button" onClick={openLogCycle} style={{ background: '#D4537E', color: 'white', border: 'none', borderRadius: 999, padding: '8px 18px', fontSize: 11, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 16 }}>Start logging →</button>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                  {['Cycles', 'Medications', 'Results', 'Milestones'].map((t) => (
                    <span key={t} style={{ background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 999, padding: '4px 12px', fontSize: 11, color: 'rgba(60,30,45,0.45)' }}>{t}</span>
                  ))}
                </div>
              </div>
            );
          }
          if (!hasStimDay) {
            return (
              <div style={cardStyle}>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                  <div>
                    <div style={labelStyle}>IVF</div>
                    <div style={headlineStyle}>Cycle started</div>
                    {subtext && <div style={subtextStyle}>{subtext}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                    <div style={{ fontSize: 11, color: 'rgba(60,30,45,0.4)', marginBottom: 4 }}>Days to retrieval</div>
                    <div style={{ fontSize: 13, color: 'rgba(60,30,45,0.3)', fontStyle: 'italic' }}>Not logged yet</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                  <span style={{ background: 'rgba(255,255,255,0.7)', border: '0.5px solid rgba(212,83,126,0.25)', borderRadius: 999, padding: '4px 12px', fontSize: 11, color: '#D4537E', fontWeight: 500 }}>● Cycle in progress</span>
                  <button type="button" onClick={openEditCycle} style={{ background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 999, padding: '4px 12px', fontSize: 11, color: 'rgba(60,30,45,0.4)', cursor: 'pointer', font: 'inherit' }}>+ Add stim day</button>
                  <button type="button" onClick={openEditCycle} style={{ background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 999, padding: '4px 12px', fontSize: 11, color: 'rgba(60,30,45,0.4)', cursor: 'pointer', font: 'inherit' }}>+ Add follicle count</button>
                </div>
              </div>
            );
          }
          return (
            <div style={cardStyle}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={labelStyle}>IVF</div>
                  <div style={headlineStyle}>{formatStimulationRangeHeadline(stimDay, stimDayStart)}</div>
                  {subtext && <div style={subtextStyle}>{subtext}</div>}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                    <span style={{ background: 'rgba(255,255,255,0.7)', border: '0.5px solid rgba(212,83,126,0.25)', borderRadius: 999, padding: '4px 12px', fontSize: 11, color: '#D4537E', fontWeight: 500 }}>● {currentStage}</span>
                    {follicleCount > 0 && <span style={{ background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 999, padding: '4px 12px', fontSize: 11, color: 'rgba(60,30,45,0.5)' }}>{follicleCount} follicles last scan</span>}
                    {medicationName && <span style={{ background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 999, padding: '4px 12px', fontSize: 11, color: 'rgba(60,30,45,0.5)' }}>💊 {medicationName}</span>}
                  </div>
                </div>
                {daysToRetrieval != null && (
                  <div style={{ flexShrink: 0, padding: '14px 20px', background: 'rgba(255,255,255,0.6)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 32, fontWeight: 500, color: '#D4537E', lineHeight: 1 }}>~{daysToRetrieval}</div>
                    <div style={{ fontSize: 11, color: 'rgba(60,30,45,0.5)', marginTop: 4 }}>days to retrieval</div>
                  </div>
                )}
              </div>
            </div>
          );
        })() : null : (
          <div style={{ background:'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', ...introCardKeyline, padding:'22px 26px', display:'flex', alignItems:'center', gap:'14px' }}>
            <div style={{ width:44, height:44, borderRadius:'50%', background:'rgba(255,255,255,0.7)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>{chapter?.icon || '🗺️'}</div>
            <div>
              <div style={{ fontSize:'14px', fontWeight:'700', color:'#2d1a22' }}>{chapter?.name || 'Your chapter'} — {cycleLabel}</div>
              <div style={{ fontSize:'12px', color:'rgba(60,30,45,0.5)', marginTop:'3px' }}>Started {startedAt} · {user?.name || 'Your'} journey</div>
            </div>
          </div>
        )}

        {/* Nova check-in — merged header includes this on demo fertility IVF; standalone bar otherwise */}
        {!(isDemo && showIVF) && (
          <NovaUnifiedPageBar
            layout="stacked"
            contextPill={(showIVF ? novaCheckInIVF : novaCheckInGeneric).contextPill}
            body={(showIVF ? novaCheckInIVF : novaCheckInGeneric).text}
            actionLabel="Open Nova →"
            onAction={() => {
              const t = (showIVF ? novaCheckInIVF : novaCheckInGeneric).text;
              setNovaDraft?.(t);
              navigate('/nova');
            }}
          />
        )}

        <div style={demoSectionCardStackStyle}>
        {/* Your story (editable summary — diagnosis, protocol, IVF/TTC details) */}
        <MyStoryJourney
          user={user}
          timeline={myStoryTimeline}
          journey={journey}
          chapter={mapJourneyTypeToBucketId(stage)}
          getJourneyTimeline={getJourneyTimeline}
          updateJourneyTimeline={updateJourneyTimeline}
          navigate={navigate}
          fontSerif="'Playfair Display', Georgia, serif"
          fontBody="'Plus Jakarta Sans', -apple-system, sans-serif"
          chapterInfo={CHAPTER_PALETTE[mapJourneyTypeToBucketId(stage)] || CHAPTER_PALETTE.ivf}
          embedded
          fertilityDemoSectionKeyline={isDemo && showIVF}
        />

        {/* Stage-specific tracker: Treatment milestones */}
        {showIVF && (
            <div
              style={{
                background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))',
                ...(isDemo && showIVF
                  ? { border: FERTILITY_DEMO_SECTION_BORDER, borderRadius: 14, boxSizing: 'border-box' }
                  : introCardKeyline),
                padding: '22px 26px',
              }}
            >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 12,
                marginBottom: 18,
              }}
            >
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                {isDemo ? (
                  <SectionHeader
                    title="Cycle 2 — Treatment milestones"
                    descriptor="Walk through IVF steps, dates, and Nova tips for each phase."
                  />
                ) : (
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#2d1a22' }}>Cycle 2 — Treatment milestones</div>
                )}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(60,30,45,0.4)', flexShrink: 0, paddingTop: isDemo ? 4 : 0 }}>
                Step {currentStep + 1} of {IVF_STEPS.length}
              </div>
            </div>
            <div style={{ display:'flex', position:'relative', marginBottom:'16px', overflowX:'auto', paddingBottom:2, WebkitOverflowScrolling:'touch' }}>
              <div style={{ position:'absolute', top:'18px', left:'18px', right:'18px', height:'2px', background:'rgba(212,83,126,0.15)' }}/>
              <div style={{ position:'absolute', top:'18px', left:'18px', height:'2px', background:'#D4537E', width: `${(currentStep / (IVF_STEPS.length - 1)) * 100}%`, transition:'width 0.5s' }}/>
              {IVF_STEPS.map((s, i) => {
                const stepDates = activeCycle?.treatmentStepDates || {};
                const stepDate = stepDates[s.id] || '';
                const dateColor = i === currentStep ? 'var(--pink-d)' : i < currentStep ? 'var(--sage-d)' : 'rgba(60,30,45,0.35)';
                return (
                  <div
                    key={s.id}
                    style={{
                      flex: '1 1 0',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      zIndex: 1,
                      minWidth: 72,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setCurrentStep(i)}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        width: '100%',
                        padding: 0,
                      }}
                      aria-label={`Set step to ${s.label}`}
                    >
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '700',
                          background: i < currentStep ? 'var(--sage-m)' : i === currentStep ? 'var(--pink-m)' : 'rgba(255,255,255,0.55)',
                          border: i < currentStep ? '2px solid var(--sage)' : i === currentStep ? '2px solid var(--pink)' : '2px solid rgba(212,83,126,0.2)',
                          color: i < currentStep ? '#085041' : i === currentStep ? 'var(--pink-dk)' : 'rgba(60,30,45,0.35)',
                          boxShadow: i === currentStep ? '0 0 0 4px var(--pink-l)' : 'none',
                        }}
                      >
                        {i < currentStep ? '✓' : i === currentStep ? '→' : i + 1}
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          color: dateColor,
                          fontWeight: i === currentStep ? '700' : '400',
                          textAlign: 'center',
                          lineHeight: '1.3',
                        }}
                      >
                        {s.label}
                      </div>
                    </button>
                    {activeCycle?.id ? (
                      <input
                        type="date"
                        value={stepDate}
                        onChange={(e) => {
                          const v = e.target.value;
                          const prev = activeCycle.treatmentStepDates || {};
                          const next = { ...prev };
                          if (v) next[s.id] = v;
                          else delete next[s.id];
                          updateCycle(activeCycle.id, { treatmentStepDates: next });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Clinic date for ${s.label}`}
                        title={stepDate ? formatClinicStepDate(stepDate) : 'Add the date your clinic gave you for this step'}
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          fontSize: 9,
                          fontFamily: 'inherit',
                          color: dateColor,
                          border: 'none',
                          background: 'transparent',
                          padding: '1px 0',
                          textAlign: 'center',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          lineHeight: 1.2,
                        }}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div style={{ background:'rgba(255,255,255,0.68)', border:'0.5px solid rgba(212,83,126,0.18)', borderRadius:'12px', padding:'16px 18px' }}>
              {(() => {
                const stepId = IVF_STEPS[currentStep]?.id;
                const stepLabel = IVF_STEPS[currentStep]?.label || 'Step';
                const subtitle = STEP_SUBTITLES[stepId] || "You're here — Nova can help with next steps.";
                const prep = stepId && IVF_STEP_PREP[stepId];
                const leftItems = STEP_WHAT_TO_EXPECT[stepId];
                const rightItems = STEP_WATCH_FOR[stepId];
                const hasStepContent = leftItems && rightItems;

                const askNovaHandler = () => {
                  if (prep?.novaPrompt) setNovaDraft(prep.novaPrompt);
                  navigate('/nova');
                };

                const rowItem = (item, last, key) => (
                  <div key={key ?? item.title} style={{ display:'flex', gap:10, alignItems:'flex-start', paddingBottom:8, marginBottom:last ? 0 : 8, borderBottom: last ? 'none' : '0.5px solid rgba(212,83,126,0.1)' }}>
                    <span style={{ fontSize:16, marginTop:-1, flexShrink:0 }}>{item.emoji}</span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:'#2d1a22' }}>{item.title}</div>
                      <div style={{ fontSize:11, color:'rgba(60,30,45,0.55)' }}>{item.sub}</div>
                    </div>
                  </div>
                );

                return (
                  <>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                      <div>
                        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize:16, fontWeight:700, color:'#2d1a22' }}>{stepLabel}</div>
                        <div style={{ fontSize:12, color:'rgba(60,30,45,0.5)', marginTop:2 }}>{subtitle}</div>
                        {activeCycle?.treatmentStepDates?.[stepId] ? (
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--pink-d)', marginTop: 6 }}>
                            Clinic date: {formatClinicStepDate(activeCycle.treatmentStepDates[stepId])}
                          </div>
                        ) : null}
                      </div>
                      <button type="button" onClick={askNovaHandler} style={{ background:'#D4537E', color:'white', border:'none', borderRadius:10, padding:'10px 20px', fontSize:13, fontWeight:600, cursor:'pointer' }}>Ask Nova →</button>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <div style={{ background:'rgba(255,255,255,0.68)', border:'0.5px solid rgba(212,83,126,0.15)', borderRadius:12, padding:'14px 16px' }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:10 }}>What to expect</div>
                        {hasStepContent ? leftItems.map((item, i) => rowItem(item, i === leftItems.length - 1, item.title)) : (prep?.bullets || ['Add your protocol + reminders below']).map((b, i, arr) => rowItem({ emoji:'•', title:b, sub:'' }, i === arr.length - 1, `bullet-${i}`))}
                      </div>
                      <div style={{ background:'rgba(255,255,255,0.68)', border:'0.5px solid rgba(212,83,126,0.15)', borderRadius:12, padding:'14px 16px' }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:10 }}>Watch for & next steps</div>
                        {hasStepContent ? rightItems.map((item, i) => rowItem(item, i === rightItems.length - 1, item.title)) : rowItem({ emoji:'💬', title:'Ask Nova about this step', sub:'Get personalized guidance for where you are right now' }, true, 'fallback-nova')}
                      </div>
                    </div>
                    {activeCycle?.id && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Cycle notes</div>
                          <button
                            type="button"
                            onClick={() => cycleNotesRef.current?.focus()}
                            style={
                              isDemo
                                ? fertilityDemoEditButton
                                : { fontSize: 12, fontWeight: 500, color: '#D4537E', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }
                            }
                          >
                            {(activeCycle.notes ?? '').trim() ? 'Edit' : 'Start logging'}
                          </button>
                        </div>
                        <textarea
                          ref={cycleNotesRef}
                          value={activeCycle.notes ?? ''}
                          onChange={e => updateCycle(activeCycle.id, { notes: e.target.value })}
                          placeholder="Notes for this cycle (protocol, side effects, questions for the clinic…)"
                          rows={4}
                          style={{ width: '100%', padding: 12, borderRadius: 10, border: '1px solid var(--border)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Medication protocol — timing and reminders embedded per medication */}
        {(showIVF || showTTC) && (
        <div style={{ background:'var(--white)', border: isDemo && showIVF ? FERTILITY_DEMO_SECTION_BORDER : '0.5px solid var(--border)', borderRadius:'14px', padding: isDemo ? '16px 20px' : 0, overflow:'hidden' }}>
          {!isDemo ? (
            <div style={{ background:'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', padding: '14px 22px', display:'flex', alignItems:'center', borderRadius:0 }}>
              <span style={{ fontSize:13, fontWeight:700, color:'#2d1a22' }}>Medication protocol</span>
            </div>
          ) : null}
          <div style={{ padding: isDemo ? 0 : 20 }}>
          {isDemo ? (
            <div style={{ marginBottom: 14 }}>
              <SectionHeader
                title="Medication protocol"
                descriptor="Add each medication with when to take it. You'll get reminders at the times you set."
              />
            </div>
          ) : (
            <div style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'14px' }}>Add each medication with when to take it. You’ll get reminders at the times you set.</div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1.2fr .6fr .6fr 1fr .5fr', gap:'8px', marginBottom:'10px', alignItems:'center' }}>
            <input value={newMed.name} onChange={e => setNewMed(v => ({ ...v, name: e.target.value }))} placeholder="Medication (e.g. Gonal‑F)" style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)' }} />
            <input value={newMed.dose} onChange={e => setNewMed(v => ({ ...v, dose: e.target.value }))} placeholder="Dose" style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)' }} />
            <input value={newMed.units} onChange={e => setNewMed(v => ({ ...v, units: e.target.value }))} placeholder="Units" style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)' }} />
            <input value={newMed.schedule} onChange={e => setNewMed(v => ({ ...v, schedule: e.target.value }))} placeholder="Schedule (e.g. nightly)" style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)' }} />
            <input type="time" value={newMed.reminderTime} onChange={e => setNewMed(v => ({ ...v, reminderTime: e.target.value }))} placeholder="Reminder" style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)' }} title="When to remind you" />
          </div>
          <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap' }}>
            <input value={newMed.startDay} onChange={e => setNewMed(v => ({ ...v, startDay: e.target.value }))} placeholder="Start day (optional)" style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)', minWidth:160 }} />
            <input value={newMed.endDay} onChange={e => setNewMed(v => ({ ...v, endDay: e.target.value }))} placeholder="End day (optional)" style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)', minWidth:160 }} />
            <button
              onClick={() => {
                if (!activeCycle?.id) return;
                if (!newMed.name.trim()) return;
                addMedication(activeCycle.id, newMed);
                setNewMed({ name: '', dose: '', units: '', schedule: '', startDay: '', endDay: '', reminderTime: '' });
              }}
              style={{ marginLeft:'auto', fontSize:'12px', fontWeight:'700', color:'white', background:'var(--pink)', border:'none', padding:'10px 14px', borderRadius:'10px', cursor:'pointer' }}
            >
              Save medication
            </button>
          </div>

          {(activeCycle?.medications || []).length === 0 ? (
            <div style={{ fontSize:'12px', color:'var(--muted)' }}>No medications saved for this cycle yet.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {(activeCycle.medications || []).map(m => {
                const start = parseInt(m.startDay, 10);
                const end = parseInt(m.endDay, 10);
                const hasRange = !Number.isNaN(start) && !Number.isNaN(end) && end >= start;
                const dayCount = hasRange ? Math.min(end - start + 1, 30) : 0;
                const medReminder = (activeCycle?.reminders || []).find(r => r.linkedMedicationId === m.id);
                const reminderLabel = medReminder ? (() => { const [h, mm] = (medReminder.time || '09:00').split(':'); const hh = parseInt(h, 10) || 9; const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh; return `${h12}:${(mm || '00').padStart(2,'0')} ${hh < 12 ? 'AM' : 'PM'}`; })() : null;
                const video = getMedicationVideo(m.name);
                const summaryParts = [
                  m.name,
                  (m.dose || m.units) ? `${m.dose || ''} ${m.units || ''}`.trim() : null,
                  m.schedule || null,
                  (m.startDay != null || m.endDay != null) ? `Days ${m.startDay || '?'}–${m.endDay || '?'}` : null,
                ].filter(Boolean);
                return (
                  <div key={m.id} style={{ padding:'12px 14px', border:'1px solid var(--border)', borderRadius:12, background:'var(--card)' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:'10px', flexWrap:'wrap' }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'13px', fontWeight:'700', color:'var(--text)' }}>{m.name}</div>
                        <div style={{ fontSize:'12px', color:'var(--muted)' }}>
                          {summaryParts.join(' · ')}
                        </div>
                        {reminderLabel && (
                          <div style={{ fontSize:'11px', color:'var(--pink-d)', marginTop:'4px', display:'flex', alignItems:'center', gap:'4px' }}>
                            🔔 Reminder: {reminderLabel}
                          </div>
                        )}
                        {video && (
                          <div style={{ marginTop:'8px' }}>
                            {video.type === 'youtube' ? (
                              <div style={{ maxWidth:360, borderRadius:8, overflow:'hidden' }}>
                                <iframe title={video.title} src={video.url} width="100%" height="200" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ display:'block' }} />
                              </div>
                            ) : (
                              <a href={video.url} target="_blank" rel="noopener noreferrer" style={{ fontSize:'12px', fontWeight:'600', color:'var(--pink)', textDecoration:'none' }}>
                                ▶ {video.title} →
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      <button onClick={() => removeMedication(activeCycle.id, m.id)} style={{ fontSize:'11px', color:'var(--muted)', background:'none', border:'none', cursor:'pointer', flexShrink:0 }}>
                        Remove
                      </button>
                    </div>
                    {hasRange && dayCount > 0 && (
                      <div style={{ display:'flex', alignItems:'center', gap:'4px', marginTop:'8px', flexWrap:'wrap' }}>
                        <span style={{ fontSize:'10px', color:'var(--muted)', marginRight:'6px' }}>Days:</span>
                        {Array.from({ length: dayCount }, (_, i) => start + i).map(d => (
                          <div
                            key={d}
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: 'var(--pink-m)',
                              border: '1px solid var(--pink)',
                              flexShrink: 0,
                            }}
                            title={`Day ${d}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>
        )}

        {(showIVF || showTTC) && (
          <YourCyclesCard
            ref={cyclesCardRef}
            journey={journey}
            addCycle={addCycle}
            updateCycle={updateCycle}
            setActiveCycleId={setActiveCycleId}
            showIVF={showIVF}
            showTTC={showTTC}
            isDemoFertility={isDemo && showIVF}
          />
        )}

        {showTTC && (
          <div style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:'14px', padding:'20px' }}>
            {isDemo ? (
              <div style={{ marginBottom: 10 }}>
                <SectionHeader
                  title="Ovulation tracker"
                  descriptor="Estimates from your cycle start and length — add reminders in the section below."
                />
              </div>
            ) : (
              <div style={{ fontSize:'13px', fontWeight:'700', color:'var(--text)', marginBottom:'10px' }}>Ovulation tracker</div>
            )}
            {!fertileWindow ? (
              <div style={{ fontSize:'12px', color:'var(--muted)', lineHeight:'1.6' }}>
                Add a cycle start date (and optionally cycle length) to estimate your fertile window.
              </div>
            ) : (
              <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                <div style={{ background:'var(--sand-2)', border:'1px solid var(--border)', borderRadius:'12px', padding:'12px 14px', minWidth:220 }}>
                  <div style={{ fontSize:'11px', color:'var(--muted)' }}>Estimated ovulation</div>
                  <div style={{ fontSize:'14px', fontWeight:'700', color:'var(--text)' }}>Day {fertileWindow.ovulationDay}</div>
                  <div style={{ fontSize:'12px', color:'var(--muted)' }}>Cycle length: {fertileWindow.cycleLength} days</div>
                </div>
                <div style={{ background:'var(--pink-l)', border:'1px solid var(--pink-m)', borderRadius:'12px', padding:'12px 14px', minWidth:260 }}>
                  <div style={{ fontSize:'11px', color:'var(--pink-d)' }}>Fertile window (estimate)</div>
                  <div style={{ fontSize:'14px', fontWeight:'700', color:'var(--pink-d)' }}>
                    {fertileWindow.fwStart.toISOString().slice(0,10)} → {fertileWindow.fwEnd.toISOString().slice(0,10)}
                  </div>
                  <div style={{ fontSize:'12px', color:'var(--pink-d)', opacity:0.85 }}>You can add reminders below.</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Appointments & other reminders (non-medication) */}
        {(showIVF || showTTC) && (
        <div style={{ background:'var(--white)', border: isDemo && showIVF ? FERTILITY_DEMO_SECTION_BORDER : '1px solid var(--border)', borderRadius:'14px' }}>
          <div style={{ padding:20 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 14,
              marginBottom: 16,
            }}
          >
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <SectionHeader
                title="Appointments & other reminders"
                descriptor={
                  isDemo
                    ? 'Fertility monitoring, baseline ultrasound, blood draws, and follicle scans.'
                    : 'Fertility monitoring appointments, baseline ultrasound, blood draws, follicle scans, etc.'
                }
              />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
              <div style={{ fontSize:'11px', color:'var(--muted)' }}>
                Browser: {notifPermission === 'unsupported' ? 'Unsupported' : notifPermission}
              </div>
              {notifPermission !== 'unsupported' && notifPermission !== 'granted' && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await Notification.requestPermission();
                      setNotifPermission(res);
                    } catch {
                      // ignore
                    }
                  }}
                  style={{ fontSize:'12px', fontWeight:'700', color:'var(--pink)', background:'var(--white)', border:'1px solid var(--pink-m)', padding:'7px 12px', borderRadius:'999px', cursor:'pointer' }}
                >
                  Enable reminders
                </button>
              )}
              {notifPermission === 'granted' && (
                <button
                  type="button"
                  onClick={() => {
                    try {
                      new Notification('OrivonAI reminder test', { body: 'Notifications are enabled.' });
                    } catch {
                      // ignore
                    }
                  }}
                  style={{ fontSize:'12px', fontWeight:'700', color:'var(--pink)', background:'var(--white)', border:'1px solid var(--pink-m)', padding:'7px 12px', borderRadius:'999px', cursor:'pointer' }}
                >
                  Test
                </button>
              )}
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap', alignItems:'center' }}>
            <input value={newReminder.title} onChange={e => setNewReminder(v => ({ ...v, title: e.target.value }))} placeholder="e.g. Fertility monitoring appointment" style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)', flex:1, minWidth:220 }} />
            <input type="time" value={newReminder.time} onChange={e => setNewReminder(v => ({ ...v, time: e.target.value }))} style={{ padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)' }} />
            <button
              onClick={() => {
                if (!activeCycle?.id) return;
                if (!newReminder.title.trim()) return;
                addReminder(activeCycle.id, {
                  title: newReminder.title,
                  time: newReminder.time,
                  linkedMedicationId: null,
                });
                setNewReminder({ title: '', time: '09:00', linkedMedicationId: '' });
              }}
              style={{ fontSize:'12px', fontWeight:'700', color:'white', background:'var(--pink)', border:'none', padding:'10px 14px', borderRadius:'10px', cursor:'pointer' }}
            >
              Add reminder
            </button>
          </div>

          {(activeCycle?.reminders || []).filter(r => !r.linkedMedicationId).length === 0 ? (
            <div style={{ fontSize:'12px', color:'var(--muted)' }}>No appointment reminders yet.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {(activeCycle.reminders || []).filter(r => !r.linkedMedicationId).map(r => (
                <div key={r.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', border:'1px solid var(--border)', borderRadius:12, background:'var(--card)' }}>
                  <div style={{ fontSize:'13px', fontWeight:'700', color:'var(--text)' }}>{r.title}</div>
                  <div style={{ fontSize:'12px', color:'var(--muted)' }}>{(() => { const [h, mm] = (r.time || '09:00').split(':'); const hh = parseInt(h, 10) || 9; const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh; return `${h12}:${(mm || '00').padStart(2,'0')} ${hh < 12 ? 'AM' : 'PM'}`; })()}</div>
                  <button onClick={() => removeReminder(activeCycle.id, r.id)} style={{ marginLeft:'auto', fontSize:'11px', color:'var(--muted)', background:'none', border:'none', cursor:'pointer' }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop:'10px', fontSize:'11px', color:'var(--muted)', lineHeight:'1.5' }}>
            Medication reminders live with each medication above. Browser reminders fire while this tab is open.
          </div>
          </div>
        </div>
        )}

        {/* Mood tracker — Today's log (shown for all chapters) */}
        <MoodTracker fertilityDemoSectionBorder={isDemo && showIVF} />
        {showTTC && (
          <div style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:'14px', padding:'20px' }}>
            <CycleCalendar
              startDate={activeCycle?.startedAt}
              onSave={({ startDate, endDate }) => {
                addLog('cycle', { startDate, endDate });
                if (activeCycle?.id && startDate) updateCycle(activeCycle.id, { startedAt: startDate });
              }}
            />
          </div>
        )}

        {/* Other chapters (placeholders for now) */}
        {(showPregnancy || showPostpartum || showBaby || showSurrogacy || showLoss) && (
          <div style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:'14px', padding:'20px' }}>
            {isDemo ? (
              <SectionHeader
                title={`${chapter?.name || 'Journey'} — coming soon`}
                descriptor="Dedicated trackers and prompts for this journey are on the roadmap. Use Nova in the sidebar for personalized support today. IVF & TTC already include cycles, meds, and reminders."
              />
            ) : (
              <>
                <div style={{ fontSize:'13px', fontWeight:'700', color:'var(--text)', marginBottom:'6px' }}>
                  {chapter?.name || 'Journey'} — coming soon
                </div>
                <div style={{ fontSize:'12px', color:'var(--muted)', lineHeight:1.6 }}>
                  Dedicated trackers and prompts for this journey are on the roadmap. Use <strong>Nova</strong> in the sidebar for personalized support today. IVF & TTC already include cycles, meds, and reminders.
                </div>
              </>
            )}
          </div>
        )}

        </div>
      </div>
      </main>
      {!mNav && wideEnough ? rightSidebar : null}
    </div>
    {!wideEnough && rightSidebar}
    {isDemo && (showIVF || showTTC) && (
      <NovaSidebarWithCollapse
        navigate={navigate}
        setNovaDraft={setNovaDraft}
        chapter={showTTC ? 'ttc' : 'fertility'}
        section="myjourney"
        className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
      />
    )}
    {mNav && mobileMenuOpen && <MobileNavDrawer onClose={() => setMobileMenuOpen(false)} />}
    </>
  );
}
